/**
 *
 * @module consoleSink
 *
 */

/* eslint no-console: "off" */
/* eslint camelcase: "off" */

import chalk from 'chalk';
import ansi_up from 'ansi_up';
import LogSink from './../logSink';
import LogLevel from './../logLevel';

/**
 *
 * @class ConsoleSink
 * @extends {LogSink}
 * @desc Sink implementation that logs to the JavaScript console.
 *
 */
export default class ConsoleSink extends LogSink {

    /**
     *
     * @param {LogLevel} logLevel
     *
     */
    constructor(logLevel) {

        super(logLevel);

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.isChalkEnabled = chalk.enabled || false;

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.hasConsole = typeof console !== 'undefined';

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.hasWindow = typeof window !== 'undefined';

    }

    /**
     *
     * @access public
     * @param {LogEvent} logEvent - The event to be logged.
     * @desc prints a given {LogEvent} to the console; provides colored output to
     * help visually aid developers (if supported on a given platform)
     * @returns {Promise<Void>}
     *
     */
    log(logEvent) {

        if (logEvent.level < this.logLevel) {
            return Promise.resolve();
        }

        const { isChalkEnabled, hasConsole, hasWindow, levelMap, transformOutput } = this;

        return new Promise((resolve) => {

            const level = LogLevel[logEvent.level];
            const message = logEvent.toString();

            if (isChalkEnabled) {
                console.log(levelMap(level)(message));
            }

            /**
             *
             * @desc for platforms with a window Object, enable chalk manually,
             * and transform the output to something the browser console can understand;
             * otherwise, fall back to a standard console log
             *
             */
            if (!isChalkEnabled) {

                if (hasWindow) {
                    chalk.enabled = true;
                    console.log(...transformOutput(levelMap(level, chalk)(message)));
                }

                if (!hasWindow && hasConsole) {
                    console.log(message);
                }

            }

            return resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} [level='none']
     * @param {Object} [instance=chalk] optional, chalk instance
     * @desc maps log levels with chalk methods
     * @see https://github.com/chalk/chalk
     *
     */
    levelMap(level = 'none', instance = chalk) {

        const map = {
            none: instance.gray,
            trace: instance.green,
            debug: instance.magenta,
            info: instance.blue,
            warn: instance.yellow,
            error: instance.underline.red
        };

        return map[level];

    }

    /**
     *
     * @access private
     * @param {String} input (for example, '\u001b[31mfoobar\u001b[39m')
     * @desc transforms a String with ANSI escape codes, exposes an Array that can
     * be consumed by a browser console implementation with the correct {LogLevel}
     * @returns {Array}
     * @see https://github.com/drudru/ansi_up
     * @note the last item in the Array must be an empty String
     *
     */
    transformOutput(input) {

        const inputHTML = ansi_up.ansi_to_html(input);
        const startTag = /<span\s+style=(['"])([^'"]*)\1\s*>/gi;
        const endTag = /<\/span>/gi;

        if (!startTag.test(inputHTML)) {
            return [
                input
            ];
        }

        const inputClean = inputHTML.replace(startTag, '%c').replace(endTag, '%c');
        const inputGroup = startTag.exec(inputHTML);
        const inputColor = inputGroup.pop();
        const inputSpacer = ' ';

        return [
            inputClean,
            inputColor,
            inputSpacer
        ];

    }

}



// WEBPACK FOOTER //
// ./src/logging/sinks/consoleSink.js